import {
  BaseQueryFn,
  EndpointBuilder,
  QueryDefinition,
  MutationDefinition,
} from '@reduxjs/toolkit/query';

import type { ICollectionApiResponse } from 'services/api/api.types';
import type {
  ILocationArgs,
  ILocationCollectionArgs,
  ILocationCollectionResponse,
  ILocationDeleteArgs,
  ILocationPostArgs,
  ILocationPutArgs,
} from './locations.api.type';
import type { ILocation } from 'interfaces/Location.interface';
import {
  serializeFilters,
  serializeFlatObject,
} from '../helper/getPaginationParams.helper';
import { transformCollectionResponse } from 'services/teamHeroApi.helpers';

export const getLocations = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  ILocationCollectionArgs,
  BaseQueryFn,
  string,
  ILocationCollectionResponse
> =>
  build.query<ILocationCollectionResponse, ILocationCollectionArgs>({
    providesTags: ['Location'],
    query: (queryArg: ILocationCollectionArgs) => {
      const paginationParamObject = {
        page: queryArg.page,
        itemsPerPage: queryArg.itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);
      return {
        url: `api/locations${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<ILocation>
    ): ILocationCollectionResponse =>
      transformCollectionResponse<ILocation>(response),
  });

export const getLocationItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<ILocationArgs, BaseQueryFn, string, ILocation> => {
  return build.query<ILocation, ILocationArgs>({
    providesTags: ['Location'],
    query: (queryArg: ILocationArgs) => ({
      url: `api/locations/${queryArg.id}`,
    }),
  });
};

export const postLocationItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<ILocationPostArgs, BaseQueryFn, string, ILocation> =>
  build.mutation<ILocation, ILocationPostArgs>({
    invalidatesTags: ['Location'],
    query: (queryArg) => ({
      url: `api/locations`,
      method: 'POST',
      body: queryArg.body,
    }),
  });

export const putLocationItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<ILocationPutArgs, BaseQueryFn, string, ILocation> =>
  build.mutation<ILocation, ILocationPutArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags ? [] : ['Location'],
    query: (queryArg) => ({
      url: `api/locations/${queryArg.id}`,
      method: 'PATCH',
      body: queryArg.body,
    }),
  });

export const deleteLocationItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<ILocationDeleteArgs, BaseQueryFn, string, unknown> =>
  build.mutation<unknown, ILocationDeleteArgs>({
    invalidatesTags: ['Location'],
    query: (queryArg) => ({
      url: `api/locations/${queryArg.id}`,
      method: 'DELETE',
    }),
  });
